import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { closeSession } from "../lib/Router";
import { ACTIVE_HEADER_VARIANT, HEADER_VARIANTS } from "../config/headerTheme";

const Container = styled.div`
  --header-primary: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].primary};
  --header-accent: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].accent};
  --header-primary-light: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].primaryLight};
  --header-menu-bg: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].menuBg};
  --header-menu-text: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].menuText};
  --header-menu-hover: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].menuHover};
  --header-divider: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].divider};

  position: relative;
  display: flex;
`;

const IconButton = styled.svg`
  width: 24px;
  height: 24px;
  cursor: pointer;
  stroke: #ffffff;
  transition: opacity 0.2s ease;
  display: block;
  &:hover {
    opacity: 0.8;
  }
`;

const MenuButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const MenuItemIcon = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Badge = styled.span`
  position: absolute;
  top: -3px;
  right: -3px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ff3b30;
  border: 1.5px solid #fff;
  box-sizing: content-box;
  z-index: 1;
`;

const Menu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: var(--header-menu-bg);
  border: 1px solid var(--header-divider);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  margin-top: 6px;
  z-index: 1000;
  min-width: 200px;
  overflow: hidden;
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--header-menu-text);
  transition: background 0.2s;

  &:hover {
    background: var(--header-menu-hover);
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  span {
    white-space: nowrap;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: var(--header-divider);
  margin: 4px 0;
`;

const LogoutItem = styled(MenuItem)`
  color: var(--header-accent);
  justify-content: center;

  &:hover {
    background: var(--header-primary-light);
  }
`;

const MobileNavMenu = ({ tabs = [], onSelectTab }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const hasAnyBadge = tabs.some((tab) => tab.badge);

  const toggleDropdown = () => setOpen(!open);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setOpen(false);
    }
  };

  const onLogout = async () => {
    await closeSession();
  };

  const handleSelect = (tab) => {
    if (onSelectTab) {
      onSelectTab(tab);
    }
    setOpen(false);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Container ref={dropdownRef}>
      <MenuButton onClick={toggleDropdown} aria-label="Menú">
        <IconButton
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M4 6H20" strokeWidth="2" strokeLinecap="round" />
          <path d="M4 12H20" strokeWidth="2" strokeLinecap="round" />
          <path d="M4 18H20" strokeWidth="2" strokeLinecap="round" />
        </IconButton>
        {hasAnyBadge && <Badge />}
      </MenuButton>

      {open && (
        <Menu>
          {tabs.map((tab) => (
            <MenuItem key={tab.id} onClick={() => handleSelect(tab)}>
              <MenuItemIcon>
                {tab.icon}
                {tab.badge && <Badge />}
              </MenuItemIcon>
              <span>{tab.label}</span>
            </MenuItem>
          ))}

          {tabs.length > 0 && <Divider />}

          <LogoutItem onClick={onLogout}>Cerrar sesión</LogoutItem>
        </Menu>
      )}
    </Container>
  );
};

export default MobileNavMenu;
